import axios from "axios";
import { useState, useEffect } from "react";
import EarnixLogo from "../components/EarnixLogo";
import { notify } from "../utils/notify";

import API from "../config.js";

const COUNTRY_TZ = {
  "India": "Asia/Kolkata", "Pakistan": "Asia/Karachi", "Bangladesh": "Asia/Dhaka",
  "Nepal": "Asia/Kathmandu", "Sri Lanka": "Asia/Colombo", "UAE": "Asia/Dubai",
  "Saudi Arabia": "Asia/Riyadh", "Kuwait": "Asia/Kuwait", "Qatar": "Asia/Qatar",
  "Bahrain": "Asia/Bahrain", "Oman": "Asia/Muscat", "Nigeria": "Africa/Lagos",
  "Kenya": "Africa/Nairobi", "Ghana": "Africa/Accra", "South Africa": "Africa/Johannesburg",
  "United States": "America/New_York", "United Kingdom": "Europe/London",
  "Canada": "America/Toronto", "Australia": "Australia/Sydney",
  "Malaysia": "Asia/Kuala_Lumpur", "Singapore": "Asia/Singapore",
  "Indonesia": "Asia/Jakarta", "Philippines": "Asia/Manila",
  "Thailand": "Asia/Bangkok", "Vietnam": "Asia/Ho_Chi_Minh",
  "China": "Asia/Shanghai", "Japan": "Asia/Tokyo", "South Korea": "Asia/Seoul",
  "Germany": "Europe/Berlin", "France": "Europe/Paris", "Italy": "Europe/Rome",
  "Brazil": "America/Sao_Paulo", "Mexico": "America/Mexico_City",
  "Turkey": "Europe/Istanbul", "Egypt": "Africa/Cairo", "Other": "UTC",
};

const G  = "#FFB800";
const G2 = "#FFD700";
const GD = "#C8860A";
const GRAD = `linear-gradient(135deg, ${G2}, ${G}, ${GD})`;

export default function Login() {
  const [tab, setTab] = useState("login");

  const [loginEmail,    setLoginEmail]    = useState("");
  const [loginPassword, setLoginPassword] = useState("");

  const [regName,     setRegName]     = useState("");
  const [regEmail,    setRegEmail]    = useState("");
  const [regPhone,    setRegPhone]    = useState("");
  const [regPassword, setRegPassword] = useState("");
  const [regRef,      setRegRef]      = useState("");
  const [regCountry,  setRegCountry]  = useState("");

  // OTP state (register)
  const [otpStep,    setOtpStep]    = useState(false);
  const [otpValue,   setOtpValue]   = useState("");
  const [otpResend,  setOtpResend]  = useState(0);

  // Forgot password state
  const [fpStep,      setFpStep]      = useState(0); // 0=hidden, 1=email, 2=otp, 3=newpass
  const [fpEmail,     setFpEmail]     = useState("");
  const [fpOtp,       setFpOtp]       = useState("");
  const [fpNewPass,   setFpNewPass]   = useState("");
  const [fpResend,    setFpResend]    = useState(0);

  const [error,   setErrorRaw]   = useState("");
  const [success, setSuccessRaw] = useState("");
  // Wrap so error/success strings also fire a toast (additive — inline UI stays).
  const setError   = (m) => { setErrorRaw(m);   if (m) notify.error(m); };
  const setSuccess = (m) => { setSuccessRaw(m); if (m) notify.success(m); };
  const [loading, setLoading] = useState(false);

  const handleLogin = async () => {
    if (!loginEmail || !loginPassword) return setError("Please fill in all fields.");
    setError(""); setLoading(true);
    try {
      const res = await axios.post(`${API}/login`, { email: loginEmail, password: loginPassword });
      localStorage.setItem("token", res.data.token);
      window.location.href = "/dashboard";
    } catch (e) {
      setError(e.response?.data?.message || "Invalid credentials. Please try again.");
    } finally { setLoading(false); }
  };

  const handleSendOtp = async () => {
    if (!regName || !regEmail || !regPassword) return setError("Name, email and password are required.");
    setError(""); setLoading(true);
    try {
      const country = regCountry || "Unknown";
      const timezone = COUNTRY_TZ[regCountry] || "Asia/Kolkata";
      const res = await axios.post(`${API}/register/send-otp`, {
        name: regName, email: regEmail,
        phone: regPhone, password: regPassword,
        referral: regRef || undefined,
        country, timezone,
      });
      setSuccess(res.data.message || "OTP sent to your email!");
      setOtpStep(true);
      setOtpResend(60);
    } catch (e) {
      setError(e.response?.data?.message || "Failed to send OTP.");
    } finally { setLoading(false); }
  };

  const handleVerifyOtp = async () => {
    if (!otpValue || otpValue.length !== 6) return setError("Enter the 6-digit OTP.");
    setError(""); setLoading(true);
    try {
      const res = await axios.post(`${API}/register`, { email: regEmail, otp: otpValue });
      setSuccess("Account created! You can now sign in.");
      setOtpStep(false); setOtpValue("");
      setFpStep(0);
      setTab("login"); setLoginEmail(regEmail);
    } catch (e) {
      setError(e.response?.data?.message || "Invalid or expired OTP.");
    } finally { setLoading(false); }
  };

  // countdown for register OTP resend
  useEffect(() => {
    if (otpResend <= 0) return;
    const t = setTimeout(() => setOtpResend(s => s - 1), 1000);
    return () => clearTimeout(t);
  }, [otpResend]);

  // countdown for forgot password OTP resend
  useEffect(() => {
    if (fpResend <= 0) return;
    const t = setTimeout(() => setFpResend(s => s - 1), 1000);
    return () => clearTimeout(t);
  }, [fpResend]);

  // Referral link: ?ref=CODE → open Sign Up tab with the code pre-filled
  useEffect(() => {
    const ref = new URLSearchParams(window.location.search).get("ref");
    if (ref) {
      setRegRef(ref.trim());
      setTab("register");
    }
    if (new URLSearchParams(window.location.search).get("join") === "1") setTab("register");
  }, []);

  const handleFpSendOtp = async () => {
    if (!fpEmail) return setError("Enter your email address.");
    setError(""); setLoading(true);
    try {
      await axios.post(`${API}/forgot-password/send-otp`, { email: fpEmail });
      setSuccess("OTP sent to your email!");
      setFpStep(2); setFpResend(60);
    } catch (e) {
      setError(e.response?.data?.message || "Failed to send OTP.");
    } finally { setLoading(false); }
  };

  const handleFpReset = async () => {
    if (!fpOtp || fpOtp.length !== 6) return setError("Enter the 6-digit OTP.");
    if (!fpNewPass || fpNewPass.length < 6) return setError("Password must be at least 6 characters.");
    setError(""); setLoading(true);
    try {
      await axios.post(`${API}/forgot-password/reset`, { email: fpEmail, otp: fpOtp, newPassword: fpNewPass });
      setSuccess("Password reset! You can now sign in.");
      setFpStep(0); setFpEmail(""); setFpOtp(""); setFpNewPass("");
    } catch (e) {
      setError(e.response?.data?.message || "Failed to reset password.");
    } finally { setLoading(false); }
  };

  return (
    <div style={s.page}>
      {/* Grid bg */}
      <div style={s.bgGrid} />
      {/* Gold glow */}
      <div style={s.bgGlow} />

      <div style={s.card}>
        {/* Gold border top */}
        <div style={s.cardTopBorder} />

        {/* Logo */}
        <div style={s.logoSection}>
          <div style={s.logoRing}>
            <EarnixLogo size={78} glow />
          </div>
          <div style={s.brandName}>EARNIX GLOBAL</div>
          <div style={s.tagline}>Next-Generation Crypto Earnings Platform</div>
        </div>

        <div style={s.divider} />

        {/* Tabs */}
        <div style={s.tabs}>
          <button
            style={{ ...s.tab, ...(tab === "login" ? s.activeTab : {}) }}
            onClick={() => { setTab("login"); setOtpStep(false); setFpStep(0); setError(""); setSuccess(""); }}>
            Sign In
          </button>
          <button
            style={{ ...s.tab, ...(tab === "register" ? s.activeTab : {}) }}
            onClick={() => { setTab("register"); setOtpStep(false); setError(""); setSuccess(""); }}>
            Create Account
          </button>
        </div>

        {error   && <Alert type="error"   msg={error} />}
        {success && <Alert type="success" msg={success} />}

        {tab === "login" && fpStep === 0 && (
          <div style={s.form}>
            <Field label="Email Address" type="email"    placeholder="you@example.com"    value={loginEmail}    onChange={setLoginEmail} />
            <Field label="Password"      type="password" placeholder="Enter your password" value={loginPassword} onChange={setLoginPassword} />
            <div style={{ textAlign: "right", marginTop: -8 }}>
              <button style={s.forgotLink} onClick={() => { setFpStep(1); setError(""); setSuccess(""); setFpEmail(loginEmail); }}>
                Forgot Password?
              </button>
            </div>
            <GoldButton loading={loading} onClick={handleLogin} label="Sign In" />
          </div>
        )}

        {tab === "login" && fpStep === 1 && (
          <div style={s.form}>
            <div style={s.otpInfo}>
              <div style={s.otpIcon}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="11" r="4" stroke="#FFB800" strokeWidth="1.8"/><path d="M12 15v5M10 18h4" stroke="#FFB800" strokeWidth="1.8" strokeLinecap="round"/><path d="M5 11a7 7 0 1 1 14 0" stroke="#FFB800" strokeWidth="1.8" strokeLinecap="round"/></svg>
              </div>
              <div>
                <div style={s.otpTitle}>Reset Password</div>
                <div style={s.otpSub}>Enter your registered email</div>
              </div>
            </div>
            <Field label="Email Address" type="email" placeholder="you@example.com" value={fpEmail} onChange={setFpEmail} />
            <GoldButton loading={loading} onClick={handleFpSendOtp} label="Send OTP" />
            <div style={{ textAlign: "center" }}>
              <button style={s.backBtn} onClick={() => { setFpStep(0); setError(""); setSuccess(""); }}>← Back to Sign In</button>
            </div>
          </div>
        )}

        {tab === "login" && fpStep === 2 && (
          <div style={s.form}>
            <div style={s.otpInfo}>
              <div style={s.otpIcon}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><rect x="2" y="4" width="20" height="16" rx="3" stroke="#FFB800" strokeWidth="1.8"/><path d="M2 8l10 6 10-6" stroke="#FFB800" strokeWidth="1.8" strokeLinecap="round"/></svg>
              </div>
              <div>
                <div style={s.otpTitle}>Check Your Email</div>
                <div style={s.otpSub}>OTP sent to <b style={{ color: G2 }}>{fpEmail}</b></div>
              </div>
            </div>
            <Field label="Enter 6-Digit OTP" type="text" placeholder="e.g. 847261" value={fpOtp} onChange={v => setFpOtp(v.replace(/\D/g,"").slice(0,6))} />
            <Field label="New Password" type="password" placeholder="Enter new password" value={fpNewPass} onChange={setFpNewPass} />
            <GoldButton loading={loading} onClick={handleFpReset} label="Reset Password" />
            <div style={s.resendRow}>
              {fpResend > 0
                ? <span style={s.resendWait}>Resend OTP in {fpResend}s</span>
                : <button style={s.resendBtn} onClick={handleFpSendOtp}>Resend OTP</button>
              }
              <button style={s.backBtn} onClick={() => { setFpStep(1); setFpOtp(""); setFpNewPass(""); setError(""); setSuccess(""); }}>← Change Email</button>
            </div>
          </div>
        )}

        {tab === "register" && !otpStep && (
          <div style={s.form}>
            <Field label="Full Name"                type="text"     placeholder="John Doe"                value={regName}     onChange={setRegName} />
            <Field label="Email Address"            type="email"    placeholder="you@example.com"         value={regEmail}    onChange={setRegEmail} />
            <Field label="Password"                 type="password" placeholder="Create a strong password" value={regPassword} onChange={setRegPassword} />
            <Field label="Referral Code (Optional)" type="text" placeholder="e.g. EGAB12" value={regRef} onChange={setRegRef} />
            <div style={fs.wrap}>
              <label style={fs.label}>Country *</label>
              <select value={regCountry} onChange={e => setRegCountry(e.target.value)} style={{ ...fs.input, cursor: "pointer" }}>
                <option value="">— Select Country —</option>
                {Object.keys(COUNTRY_TZ).map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <GoldButton loading={loading} onClick={handleSendOtp} label="Send OTP" />
          </div>
        )}

        {tab === "register" && otpStep && (
          <div style={s.form}>
            <div style={s.otpInfo}>
              <div style={s.otpIcon}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><rect x="2" y="4" width="20" height="16" rx="3" stroke="#FFB800" strokeWidth="1.8"/><path d="M2 8l10 6 10-6" stroke="#FFB800" strokeWidth="1.8" strokeLinecap="round"/></svg>
              </div>
              <div>
                <div style={s.otpTitle}>Check Your Email</div>
                <div style={s.otpSub}>OTP sent to <b style={{ color: G2 }}>{regEmail}</b></div>
              </div>
            </div>
            <Field label="Enter 6-Digit OTP" type="text" placeholder="e.g. 847261" value={otpValue} onChange={v => setOtpValue(v.replace(/\D/g,"").slice(0,6))} />
            <GoldButton loading={loading} onClick={handleVerifyOtp} label="Verify & Create Account" />
            <div style={s.resendRow}>
              {otpResend > 0
                ? <span style={s.resendWait}>Resend OTP in {otpResend}s</span>
                : <button style={s.resendBtn} onClick={handleSendOtp}>Resend OTP</button>
              }
              <button style={s.backBtn} onClick={() => { setOtpStep(false); setOtpValue(""); setError(""); setSuccess(""); }}>← Change Details</button>
            </div>
          </div>
        )}

        <p style={s.footer}>© {new Date().getFullYear()} Earnix Global. All rights reserved.</p>
      </div>
    </div>
  );
}

function Field({ label, type, placeholder, value, onChange }) {
  const [focused, setFocused] = useState(false);
  return (
    <div style={fs.wrap}>
      <label style={fs.label}>{label}</label>
      <input type={type} placeholder={placeholder} value={value}
        onChange={e => onChange(e.target.value)}
        onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
        style={{ ...fs.input, ...(focused ? fs.focused : {}) }} />
    </div>
  );
}

function GoldButton({ loading, onClick, label }) {
  return (
    <button style={{ ...s.btn, opacity: loading ? 0.65 : 1, cursor: loading ? "not-allowed" : "pointer" }}
      onClick={onClick} disabled={loading}>
      {loading
        ? <span style={s.spinner} />
        : <span style={{ letterSpacing: 2 }}>{label}</span>}
    </button>
  );
}

function Alert({ type, msg }) {
  const isErr = type === "error";
  return (
    <div style={{
      background: isErr ? "rgba(255,80,80,0.07)"  : "rgba(255,184,0,0.07)",
      border:     isErr ? "1px solid rgba(255,80,80,0.25)" : "1px solid rgba(255,184,0,0.3)",
      color:      isErr ? "#ff7777" : G2,
      padding: "10px 14px", borderRadius: 8,
      fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree', sans-serif", fontSize: 13,
      display: "flex", alignItems: "center", gap: 10,
      marginBottom: 8,
    }}>
      <span>{isErr ? "⚠" : "✓"}</span>{msg}
    </div>
  );
}

const s = {
  page: {
    minHeight: "100vh", background: "#03050a",
    display: "flex", alignItems: "center",
    justifyContent: "center",
    padding: "24px 16px",
    position: "relative", overflow: "hidden",
  },
  bgGrid: {
    position: "absolute", inset: 0,
    backgroundImage: `
      linear-gradient(rgba(255,184,0,0.025) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255,184,0,0.025) 1px, transparent 1px)
    `,
    backgroundSize: "48px 48px",
    pointerEvents: "none",
  },
  bgGlow: {
    position: "absolute", top: "30%", left: "50%",
    transform: "translate(-50%, -50%)",
    width: 700, height: 500,
    background: "radial-gradient(ellipse, rgba(255,184,0,0.07) 0%, transparent 65%)",
    pointerEvents: "none",
  },
  card: {
    position: "relative", width: "100%", maxWidth: 440,
    background: "rgba(8,6,3,0.97)",
    border: "1px solid rgba(255,184,0,0.18)",
    borderRadius: 16, padding: "40px 36px",
    boxShadow: "0 0 80px rgba(255,184,0,0.08), 0 24px 80px rgba(0,0,0,0.7)",
    overflow: "hidden",
  },
  cardTopBorder: {
    position: "absolute", top: 0, left: 0, right: 0, height: 2,
    background: `linear-gradient(90deg, transparent, ${G2}, ${G}, ${GD}, transparent)`,
  },
  logoSection: { textAlign: "center", marginBottom: 24 },
  logoRing: {
    width: 90, height: 90,
    margin: "0 auto 14px",
    display: "flex", alignItems: "center", justifyContent: "center",
  },
  brandName: {
    fontFamily: "'Figtree', sans-serif",
    fontWeight: 900, fontSize: 22,
    background: `linear-gradient(135deg, ${G2}, ${G}, ${GD})`,
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    letterSpacing: 4, marginBottom: 6,
  },
  tagline: {
    fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree', sans-serif",
    fontSize: 12, color: "rgba(200,160,60,0.6)", letterSpacing: 0.5,
  },
  divider: {
    height: 1,
    background: "linear-gradient(90deg, transparent, rgba(255,184,0,0.25), transparent)",
    marginBottom: 24,
  },
  tabs: {
    display: "flex", background: "rgba(0,0,0,0.4)",
    borderRadius: 8, padding: 3, marginBottom: 24,
    border: "1px solid rgba(255,184,0,0.1)",
  },
  tab: {
    flex: 1, padding: "10px",
    fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree', sans-serif",
    fontWeight: 600, fontSize: 13,
    color: "rgba(200,160,60,0.5)",
    background: "none", border: "none",
    borderRadius: 6, cursor: "pointer",
    letterSpacing: 0.5, transition: "all 0.2s",
  },
  activeTab: {
    color: "#0a0800",
    background: `linear-gradient(135deg, ${G2}, ${G})`,
    boxShadow: "0 2px 14px rgba(255,184,0,0.4)",
  },
  form: { display: "flex", flexDirection: "column", gap: 16 },
  btn: {
    width: "100%", padding: "14px",
    fontFamily: "'Figtree', sans-serif",
    fontWeight: 700, fontSize: 13,
    color: "#0a0800",
    background: `linear-gradient(135deg, ${G2}, ${G}, ${GD})`,
    border: "none", borderRadius: 8, cursor: "pointer",
    marginTop: 4,
    boxShadow: "0 4px 24px rgba(255,184,0,0.4)",
    display: "flex", alignItems: "center",
    justifyContent: "center", gap: 8,
    transition: "all 0.2s",
  },
  spinner: {
    display: "inline-block", width: 16, height: 16,
    border: "2px solid rgba(0,0,0,0.2)",
    borderTop: "2px solid #0a0800",
    borderRadius: "50%",
    animation: "spin 0.7s linear infinite",
  },
  footer: {
    fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree', sans-serif",
    fontSize: 11, color: "rgba(200,160,60,0.3)",
    textAlign: "center", marginTop: 28, letterSpacing: 0.3,
  },
  otpInfo: {
    display: "flex", alignItems: "center", gap: 14,
    background: "rgba(255,184,0,0.05)",
    border: "1px solid rgba(255,184,0,0.2)",
    borderRadius: 10, padding: "14px 16px",
  },
  otpIcon: { fontSize: 28, lineHeight: 1, display: "flex", alignItems: "center", justifyContent: "center", width: 40, height: 40, background: "rgba(255,184,0,0.12)", borderRadius: 8, flexShrink: 0 },
  otpTitle: {
    fontFamily: "'Figtree', sans-serif",
    fontWeight: 700, fontSize: 14, color: G2, marginBottom: 3,
  },
  otpSub: {
    fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree', sans-serif",
    fontSize: 12, color: "rgba(200,160,60,0.6)",
  },
  resendRow: {
    display: "flex", justifyContent: "space-between",
    alignItems: "center", marginTop: -4,
  },
  resendWait: {
    fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree', sans-serif",
    fontSize: 12, color: "rgba(200,160,60,0.45)",
  },
  resendBtn: {
    fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree', sans-serif",
    fontSize: 12, color: G, background: "none", border: "none",
    cursor: "pointer", textDecoration: "underline", padding: 0,
  },
  backBtn: {
    fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree', sans-serif",
    fontSize: 12, color: "rgba(200,160,60,0.5)", background: "none",
    border: "none", cursor: "pointer", padding: 0,
  },
  forgotLink: {
    fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree', sans-serif",
    fontSize: 12, color: G, background: "none", border: "none",
    cursor: "pointer", padding: 0, textDecoration: "underline",
  },
};

const fs = {
  wrap:  { display: "flex", flexDirection: "column", gap: 6 },
  label: {
    fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree', sans-serif",
    fontWeight: 600, fontSize: 11,
    color: "rgba(255,184,0,0.7)",
    letterSpacing: 1, textTransform: "uppercase",
  },
  input: {
    padding: "12px 14px",
    fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree', sans-serif",
    fontSize: 14, color: "#f0e6c8",
    background: "rgba(0,0,0,0.5)",
    border: "1px solid rgba(255,184,0,0.12)",
    borderRadius: 8, outline: "none",
    transition: "all 0.2s",
  },
  focused: {
    border: "1px solid rgba(255,184,0,0.5)",
    boxShadow: "0 0 0 3px rgba(255,184,0,0.08)",
    background: "rgba(255,184,0,0.03)",
  },
};

